import React, { useEffect, useState } from "react";
import {
  AppBar, Toolbar, Box, useTheme,
  Breadcrumbs, Chip, Fade, Typography, Avatar, Menu, MenuItem, Divider,
  ListItemIcon,
} from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import HomeIcon from "@mui/icons-material/Home";
import DashboardIcon from "@mui/icons-material/Dashboard";
import PeopleIcon from "@mui/icons-material/People";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import HistoryIcon from "@mui/icons-material/History";
import LogoutIcon from "@mui/icons-material/Logout";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";

import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import logo from "../assets/logoNouha.png";
import AnimatedLogo from "./Elements/AnimatedLogo";
import { useStaffAuth } from "../contexts/StaffAuthContext";

const iconSx = { fontSize: 16 };
const PATH_MAP = {
  "/staff": { label: "Bons de livraison", icon: <DashboardIcon sx={iconSx} /> },
  "/staff/clients": { label: "Clients", icon: <PeopleIcon sx={iconSx} /> },
  "/staff/users": { label: "Utilisateurs", icon: <AssignmentIndIcon sx={iconSx} /> },
  "/staff/historiques": { label: "Historique", icon: <HistoryIcon sx={iconSx} /> },
};

export default function StaffLayout() {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [blReference, setBlReference] = useState(null);
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const { user, isAdmin, logout } = useStaffAuth();

  useEffect(() => { setMenuAnchor(null); }, [location.pathname]);

  // Reset la reference courante des qu'on quitte une page de detail
  useEffect(() => {
    if (!location.pathname.startsWith("/staff/bls/") || location.pathname === "/staff/bls/new") {
      setBlReference(null);
    }
  }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate("/staff/login");
  };

  const handleNav = (path) => {
    setMenuAnchor(null);
    navigate(path);
  };

  const initials = (user?.name || user?.email || "?")
    .split(/\s+/).filter(Boolean).slice(0, 2)
    .map((s) => s[0]?.toUpperCase()).join("");

  const getBreadcrumbs = () => {
    const path = location.pathname;
    const home = { label: "Bons de livraison", path: "/staff", icon: <HomeIcon sx={iconSx} /> };

    if (path.startsWith("/staff/bls/")) {
      const label = blReference || (id ? `BL #${id}` : "Détail");
      return [
        home,
        { label, path, icon: <LocalShippingIcon sx={iconSx} />, current: true },
      ];
    }
    const info = PATH_MAP[path] || { label: "Page", icon: null };
    if (path === "/staff") return [{ ...home, current: true }];
    return [home, { label: info.label, path, icon: info.icon, current: true }];
  };

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          background: "linear-gradient(120deg, #f7fff4 0%, #d0f0bb 100%)",
          borderBottom: "3px solid #6db43d",
          zIndex: theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar sx={{ position: "relative" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Fade in timeout={1200}>
              <Box sx={{ display: { xs: "none", md: "flex" }, alignItems: "center", gap: 1 }}>
                <Breadcrumbs
                  separator={<NavigateNextIcon fontSize="small" sx={{ color: "#6db43d" }} />}
                  sx={{
                    flexWrap: "wrap",
                    rowGap: 0.5,
                    "& .MuiBreadcrumbs-separator": { color: "#6db43d" },
                  }}
                >
                  {getBreadcrumbs().map((crumb, i) => (
                    <Chip
                      key={i}
                      label={crumb.label}
                      icon={crumb.icon}
                      onClick={() => !crumb.current && navigate(crumb.path)}
                      sx={{
                        backgroundColor: crumb.current ? "rgba(109, 180, 61, 0.15)" : "rgba(109, 180, 61, 0.08)",
                        color: crumb.current ? "#6db43d" : "#666",
                        fontWeight: crumb.current ? 600 : 500,
                        cursor: crumb.current ? "default" : "pointer",
                        transition: "all 0.3s ease",
                        "&:hover": !crumb.current ? {
                          backgroundColor: "rgba(109, 180, 61, 0.2)",
                          transform: "translateY(-1px)",
                        } : {},
                        "& .MuiChip-icon": { color: crumb.current ? "#6db43d" : "#666" },
                      }}
                    />
                  ))}
                </Breadcrumbs>
              </Box>
            </Fade>
          </Box>

          {/* Logo centre */}
          <Box sx={{
            position: "absolute",
            left: "50%",
            transform: "translateX(-50%)",
            display: { xs: "none", sm: "block" },
          }}>
            <AnimatedLogo
              src={logo}
              height={40}
              onClick={() => navigate("/staff")}
              variant="default"
              showAnimation
            />
          </Box>
          <Box sx={{
            flexGrow: 1,
            display: { xs: "flex", sm: "none" },
            justifyContent: "center",
          }}>
            <AnimatedLogo src={logo} height={32} onClick={() => navigate("/staff")} showAnimation />
          </Box>

          <Box sx={{
            position: { xs: "static", sm: "absolute" },
            right: { sm: 20 },
            display: "flex",
            alignItems: "center",
            gap: 1,
            ml: { xs: "auto", sm: 0 },
          }}>
            {isAdmin && (
              <Chip
                icon={<AdminPanelSettingsIcon />}
                label="Admin"
                size="small"
                sx={{
                  bgcolor: "rgba(242, 124, 30, 0.15)",
                  color: "#c75a06",
                  fontWeight: 600,
                  display: { xs: "none", sm: "flex" },
                }}
              />
            )}
            <Box
              onClick={(e) => setMenuAnchor(e.currentTarget)}
              sx={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 1 }}
            >
              <Avatar sx={{ bgcolor: "#6db43d", width: 36, height: 36, fontSize: 14, fontWeight: 600 }}>
                {initials}
              </Avatar>
            </Box>
            <Menu
              anchorEl={menuAnchor}
              open={!!menuAnchor}
              onClose={() => setMenuAnchor(null)}
              PaperProps={{ sx: { minWidth: 220 } }}
            >
              <MenuItem disabled>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{user?.name}</Typography>
                  <Typography variant="caption" color="text.secondary">{user?.email}</Typography>
                </Box>
              </MenuItem>
              <Divider />
              <MenuItem onClick={() => handleNav("/staff")}>
                <ListItemIcon><DashboardIcon fontSize="small" sx={{ color: "#6db43d" }} /></ListItemIcon>
                Bons de livraison
              </MenuItem>
              <MenuItem onClick={() => handleNav("/staff/historiques")}>
                <ListItemIcon><HistoryIcon fontSize="small" sx={{ color: "#6db43d" }} /></ListItemIcon>
                Historique
              </MenuItem>
              {isAdmin && (
                <MenuItem onClick={() => handleNav("/staff/clients")}>
                  <ListItemIcon><PeopleIcon fontSize="small" sx={{ color: "#6db43d" }} /></ListItemIcon>
                  Clients
                </MenuItem>
              )}
              {isAdmin && (
                <MenuItem onClick={() => handleNav("/staff/users")}>
                  <ListItemIcon><AssignmentIndIcon fontSize="small" sx={{ color: "#6db43d" }} /></ListItemIcon>
                  Utilisateurs
                </MenuItem>
              )}
              <Divider />
              <MenuItem onClick={handleLogout}>
                <ListItemIcon><LogoutIcon fontSize="small" sx={{ color: "#f27c1e" }} /></ListItemIcon>
                Se déconnecter
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3 },
          mt: 8,
          bgcolor: "#f9fafc",
          minHeight: "100vh",
        }}
      >
        <Outlet context={{ setBlReference }} />
      </Box>
    </Box>
  );
}
